'use client';

import useSWR, { mutate } from 'swr';
import { fleetApi } from '../api/axiosClient';
import FilterBuilder from '../utils/filterBuilder';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateTireSizeList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/tire-size/search');
};

export const useTireSizeDataList = (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue) => {
  const isFetchAll = page === undefined;

  const companies = (selectedCompanies || []).map((c) => c.value).join(',');
  const start = isFetchAll ? 0 : page * rowsPerPage;

  const payload = useMemo(() => {
    if (isFetchAll) return null;
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .addJoin('company', ['name'])
      .addJoin('vehicle_type', ['name'])
      .setOrder(['code_internal'], true);

    const searchVal = searchValue ? String(searchValue).trim() : '';
    if (searchKey === 'code' || searchKey === 'code_internal' || !searchKey) {
      if (searchVal) {
        fb.like('code', searchVal, 'OR');
        fb.like('code_internal', searchVal, 'OR');
      }
    } else if (searchVal) {
      fb.like(searchKey, searchVal, 'AND');
    }

    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companies, isFetchAll]);

  const key = useMemo(() => ['/tire-size/search', JSON.stringify(payload)], [payload]);
  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  return {
    ...normalizeApiResponse(data, error, isLoading),
    refetch: () => mutate(key)
  };
};

export const getTireSizeById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.get(`/tire-size/${id}${getTzParam()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch tire size by ID:', error);
    throw error;
  }
};

export const createTireSize = async (formData = {}) => {
  try {
    const response = await fleetApi.post(`/tire-size/new${getTzParam()}`, formData);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireSizeList();
    return response.data;
  } catch (error) {
    console.error('Error creating tire size:', error);
    throw error;
  }
};

export const updateTireSize = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.put(`/tire-size/edit/${id}${getTzParam()}`, formData);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireSizeList();
    mutate(`/tire-size/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error updating tire size:', error);
    throw error;
  }
};

export const deleteTireSize = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.delete(`/tire-size/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireSizeList();
    return response.data;
  } catch (error) {
    console.error('Error deleting tire size:', error);
    throw error;
  }
};
